/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {providers} from '@logilab/cwclientlibjs';
import {
    EntityRelationsSpecification,
    EntityRelationsChanges,
} from './EntityRelationsEditor';
import {EDITION_TIMEOUT} from './commons';

/**
 * Applies changes to a list entities
 * @param entities A list of entities
 * @param changes The list of changes to apply
 */
export function applyChanges(
    entities: providers.Entity[],
    changes: EntityRelationsChanges[]
): providers.Entity[] {
    let result = entities.filter((entity) => {
        let found = false;
        for (let i = 0; i !== changes.length; i++) {
            for (let j = 0; j !== changes[i].removed.length; j++) {
                if (changes[i].removed[j]['@eid'] === entity['@eid']) {
                    found = true;
                    break;
                }
            }
            if (found) break;
        }
        return !found;
    });
    for (let i = 0; i !== changes.length; i++) {
        result = result.concat(...changes[i].added);
    }
    return result;
}

/**
 * Gets the already related entities, taking into account a stack of changes
 * @param specification The specification
 * @param limit The maximum number of items to retrieve
 * @param changes The stack of changes to be applied
 */
export function lookupEntitiesRelatedWithChanges(
    specification: EntityRelationsSpecification,
    limit: number,
    changes: EntityRelationsChanges[]
): Promise<providers.Entity[]> {
    if (limit > 0) {
        let newLimit = limit;
        for (let i = 0; i !== changes.length; i++) {
            newLimit += changes[i].removed.length - changes[i].added.length;
        }
        if (newLimit <= 0) {
            // more entities to be added by the changes than the original limit ...
            return new Promise((resolve, reject) =>
                resolve(applyChanges([], changes))
            );
        } else {
            // request with the new limit
            return lookupEntitiesRelated(specification, newLimit).then(
                (entities) => applyChanges(entities, changes)
            );
        }
    } else {
        return lookupEntitiesRelated(specification, -1).then((entities) =>
            applyChanges(entities, changes)
        );
    }
}

/**
 * Loads a list of rows [x, t] where x an eid and t is the eid of the entity's type as the result of a lookup
 * @param specification The original specification for the relatable entities
 * @param rows The [x, t] rows
 */
export function lookupLoadFromResults(
    specification: EntityRelationsSpecification,
    rows: [number, number][]
): Promise<providers.Entity[]> {
    return Promise.all(
        rows.map((row) => {
            let [eid, type] = row;
            return specification.schemaProvider
                .getEntitySchemaById(type)
                .then((schema) =>
                    schema === null
                        ? null
                        : specification.entityProvider.getEntityGivenSchema(
                              eid,
                              schema
                          )
                );
        })
    ).then((data) => {
        return data.filter((e) => e !== null) as any;
    });
}

/**
 * Lookups relatable entities with the specified criteria
 * @param specification The original specification for the related entities
 * @param fieldName The name of the field to use as a criteria
 * @param value The value to look for int he field
 * @param limit The maximum number of results
 */
export function lookupEntitiesByField(
    specification: EntityRelationsSpecification,
    fieldName: string,
    value: string,
    limit: number
): Promise<providers.Entity[]> {
    let typeEids = specification.relations.map((relation) =>
        specification.entityIsSubject ? relation.to : relation.from
    );
    return specification.rqlClient
        .queryRows(
            `Any X,T ${
                limit > 0 ? `LIMIT ${limit}` : ''
            } WHERE X is T, T eid IN (${typeEids
                .map((t) => `'${t}'`)
                .join(',')})${
                value.length > 0 ? `, X ${fieldName} ILIKE %(pattern)s` : ''
            }`,
            {pattern: `%${value}%`}
        )
        .then((rows) =>
            lookupLoadFromResults(specification, rows as [number, number][])
        );
}

/**
 * Gets the already related entities
 * @param specification The specification
 * @param limit The maximum number of items to retrieve
 */
export function lookupEntitiesRelated(
    specification: EntityRelationsSpecification,
    limit: number
): Promise<providers.Entity[]> {
    if (specification.entityIsSubject) {
        let rows: [number, number][] = [];
        for (let i = 0; i !== specification.relations.length; i++) {
            let relation = specification.relations[i];
            let relationName = providers.getUniqueRelationName(relation);
            let values: number[] = specification.entity[relationName];
            for (let j = 0; j !== values.length; j++) {
                rows.push([values[j], relation.to]);
                if (rows.length >= limit) break;
            }
            if (rows.length >= limit) break;
        }
        return lookupLoadFromResults(specification, rows);
    } else {
        return specification.rqlClient
            .queryRows(
                `Any X,XT ${limit > 0 ? `LIMIT ${limit}` : ''} WHERE X ${
                    specification.relations[0].name
                } Y, Y eid %(y)s, X is XT`,
                {y: specification.entity['@eid']}
            )
            .then((rows) =>
                lookupLoadFromResults(specification, rows as [number, number][])
            );
    }
}

/**
 * The parameters for the component responsible for getting the input for looking for related entities
 */
export interface EntityRelationsLookupProps {
    /**
     * The specification for the related entities
     */
    specification: EntityRelationsSpecification;
    /**
     * The CSS class name(s) for the component
     */
    className: string;
    /**
     * The limit when looking up relatable entities
     */
    lookupLimitRelatable: number;
    /**
     * The limit when looking up already related entities
     */
    lookupLimitRelated: number;
    /**
     * Handles event when the selection is currently loading the entities
     */
    onLookupLoading: () => void;
    /**
     * Handles event when relatable candidates have been found
     * @param result The result of the lookup
     */
    onLookupResult: (result: providers.Entity[]) => void;
}

/**
 * The parameters for the component responsible for getting the input for looking for related entities
 */
export interface DefaultEntityRelationsLookupProps
    extends EntityRelationsLookupProps {
    /**
     * Lookups relatable entities using the specified input
     * @param specification The specification for the related entities
     * @param input The current input string
     * @param lookupLimit The limit to the number of entities to retrieve when looking them up
     */
    lookupRelatableEntities: (
        specification: EntityRelationsSpecification,
        input: string,
        lookupLimit: number
    ) => Promise<providers.Entity[]>;
    /**
     * Lookups already related entities
     * @param specification The specification for the related entities
     * @param lookupLimit The limit to the number of entities to retrieve when looking them up
     */
    lookupRelatedEntities: (
        specification: EntityRelationsSpecification,
        lookupLimit: number
    ) => Promise<providers.Entity[]>;
}

/**
 * The state for the component responsible for getting the input for looking for related entities
 */
export interface DefaultEntityRelationsLookupState {
    /**
     * The current input string
     */
    input: string;
}

/**
 * The default props for the component responsible for getting the input for looking for related entities
 */
export const DEFAULT_ENTITY_RELATIONS_LOOKUP_DEFAULT_PROPS = {
    className: '',
    lookupLimitRelatable: 5,
    lookupLimitRelated: -1,
    lookupRelatableEntities: (
        specification: EntityRelationsSpecification,
        input: string,
        lookupLimit: number
    ) => lookupEntitiesByField(specification, 'name', input, lookupLimit),
    lookupRelatedEntities: lookupEntitiesRelated,
};

/**
 * The component responsible for getting the input for looking for related entities
 */
export class DefaultEntityRelationsLookup extends React.Component<
    DefaultEntityRelationsLookupProps,
    DefaultEntityRelationsLookupState
> {
    public static defaultProps = DEFAULT_ENTITY_RELATIONS_LOOKUP_DEFAULT_PROPS;
    state: DefaultEntityRelationsLookupState;
    timeout: number | undefined;
    _isMounted: boolean;

    constructor(props: Readonly<DefaultEntityRelationsLookupProps>) {
        super(props);
        this.timeout = undefined;
        this._isMounted = false;
        this.state = {
            input: '',
        };
        this.onInputChanged = this.onInputChanged.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        window.clearTimeout(this.timeout);
        this._isMounted = false;
    }

    onInputChanged(event: React.ChangeEvent<HTMLInputElement>) {
        const input = event.currentTarget.value;
        this.setState({input: input});
        clearTimeout(this.timeout);
        let self = this;
        this.timeout = window.setTimeout(() => {
            if (self._isMounted) {
                self.props.onLookupLoading();
                if (input.length > 0) {
                    self.props
                        .lookupRelatableEntities(
                            self.props.specification,
                            input,
                            self.props.lookupLimitRelatable
                        )
                        .then((entities) => {
                            if (self._isMounted) {
                                self.props.onLookupResult(entities);
                            }
                        })
                        .catch((reason) => {
                            console.log(reason);
                        });
                } else {
                    self.props
                        .lookupRelatedEntities(
                            self.props.specification,
                            self.props.lookupLimitRelated
                        )
                        .then((entities) => {
                            if (self._isMounted) {
                                self.props.onLookupResult(entities);
                            }
                        })
                        .catch((reason) => {
                            console.log(reason);
                        });
                }
            }
        }, EDITION_TIMEOUT);
    }

    render() {
        return (
            <input
                type="text"
                value={this.state.input}
                className={this.props.className}
                onChange={this.onInputChanged}
            />
        );
    }
}
